/**
 * Procurement list page — `/stock/procurement`.
 *
 * Replaces the legacy refills list with the unified procurement state
 * machine. The page is a thin filter shell around `api.listProcurements`:
 *
 *   - Per-column filters: State (AntD built-in multi-select from a fixed
 *     list) + Supplier (custom filterDropdown with a contains-Input).
 *     The delivery-date From/To range stays above the table (it's a
 *     range, not a column-level predicate, so it doesn't fit the
 *     column-filter pattern). Filter state persists in the query string
 *     via `useWorkspaceUrlState({ namespace: 'p' })` so reloads (and
 *     deep links from email reports) survive.
 *   - Table columns: state pill, supplier, delivery date, received at,
 *     line count, total cost, total Pfand, updated_at. Default sort is
 *     `updated_at desc`, matching the spec (`TimestampMixin`-driven
 *     "most recently touched" feel). A footer summary row sums Lines /
 *     Total cost / Total Pfand across the currently-displayed rows
 *     (after both the external date filter AND the column filters).
 *   - Single CTA: `+ New Draft` creates an empty draft procurement and
 *     navigates straight to its detail page. All editing — supplier,
 *     lines, transition to received, hard-delete — happens there.
 *
 * Sorting / filtering happens client-side after the API filter narrows
 * the result set (limit=200; if you have more open procurements than
 * that you have other problems).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Input,
  Space,
  Table,
  Typography,
} from "antd";
import type { ColumnsType, ColumnType } from "antd/es/table";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import {
  ApiError,
  type ProcurementRow,
  type ProcurementState,
  api,
} from "../../api/client";
import { StatePill } from "../../components/stock/StatePill";
import { useWorkspaceUrlState } from "../../components/workspace";
import { formatCents, formatCompactDateTime } from "../../utils/dateFormat";

const { Title, Text } = Typography;

const STATE_OPTIONS: { value: ProcurementState; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "received", label: "Received" },
  { value: "cancelled", label: "Cancelled" },
];

export const ProcurementPage = () => {
  const navigate = useNavigate();
  const { state: urlState, replace } = useWorkspaceUrlState({ namespace: "p" });

  // Filter state, mirrored to / from URL via the workspace hook.
  const stateFilter = useMemo<ProcurementState[]>(() => {
    const raw = urlState.filters.state;
    if (!raw) return [];
    return raw
      .split(",")
      .filter((s): s is ProcurementState =>
        ["draft", "received", "cancelled"].includes(s),
      );
  }, [urlState.filters.state]);
  const supplierFilter = urlState.filters.supplier ?? "";
  const dateFrom = urlState.filters.from ?? "";
  const dateTo = urlState.filters.to ?? "";

  const setDateRange = (from: string, to: string) =>
    replace({ filters: { ...urlState.filters, from, to } });

  // Server data.
  const [rows, setRows] = useState<ProcurementRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const procRows = await api.listProcurements({
        // No per-field server filtering yet — the API filters on a
        // single supplier-canon and state at a time, and we want
        // multi-state. Pull a generous slice and filter client-side.
        limit: 200,
      });
      setRows(procRows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Client-side filtering — ONLY for the delivery-date range. The State
  // and Supplier predicates are now declared as column-level filters
  // (filteredValue + onFilter below), so AntD applies them on the
  // post-`dataSource` row set. Keeping date filtering external avoids
  // adding a 4th column for it just to use the column-filter machinery.
  const filteredRows = useMemo(() => {
    let r = rows;
    if (dateFrom) {
      r = r.filter((row) => {
        const d = row.delivery_date ?? row.received_at?.slice(0, 10) ?? null;
        return d !== null && d >= dateFrom;
      });
    }
    if (dateTo) {
      r = r.filter((row) => {
        const d = row.delivery_date ?? row.received_at?.slice(0, 10) ?? null;
        return d !== null && d <= dateTo;
      });
    }
    return r;
  }, [rows, dateFrom, dateTo]);

  const onCreateDraft = async () => {
    setCreating(true);
    try {
      const created = await api.createProcurement({
        state: "draft",
        supplier: null,
        invoice_ref: null,
        delivery_date: null,
        comment: null,
        lines: [],
      });
      navigate(`/stock/procurement/${created.id}`);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // Columns. Total ordering: #, State, Supplier, Delivery date, Received at,
  // Lines, Total cost, Total Pfand, Updated, Bill total → 10 columns. The
  // footer summary's colSpan / cell indexes below depend on this layout.
  const columns: ColumnsType<ProcurementRow> = [
    {
      title: "#",
      dataIndex: "id",
      width: 70,
      render: (id: number) => <Text type="secondary">#{id}</Text>,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "State",
      dataIndex: "state",
      width: 110,
      render: (state: ProcurementState) => <StatePill state={state} />,
      sorter: (a, b) => a.state.localeCompare(b.state),
      // AntD-native multi-select filter populated from the canonical
      // state options. `filteredValue` keeps the dropdown in sync with
      // the URL state on reload / external navigation.
      filters: STATE_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
      filteredValue: stateFilter.length > 0 ? stateFilter : null,
      onFilter: (value, record) => record.state === value,
    },
    {
      title: "Supplier",
      dataIndex: "supplier",
      render: (v: string | null) => v ?? "—",
      sorter: (a, b) => (a.supplier ?? "").localeCompare(b.supplier ?? ""),
      // Custom filterDropdown: a contains-Input (case-insensitive). The
      // selectedKeys array carries the (single) needle; Reset clears it.
      filteredValue: supplierFilter ? [supplierFilter] : null,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Supplier contains…"
            value={selectedKeys[0] as string}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => confirm()}
            style={{ width: 200, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button type="primary" size="small" onClick={() => confirm()}>
              Apply
            </Button>
            <Button
              size="small"
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        (record.supplier ?? "")
          .toLowerCase()
          .includes(String(value).toLowerCase()),
    },
    {
      // Split column (Fix D.2): delivery date is the operator-entered
      // YYYY-MM-DD (no time component); received_at is the system-set
      // timestamp captured when the state machine transitioned into
      // received. They mean different things and were confusing combined.
      title: "Delivery date",
      dataIndex: "delivery_date",
      width: 130,
      render: (d: string | null) => d ?? <Text type="secondary">—</Text>,
      sorter: (a, b) =>
        (a.delivery_date ?? "").localeCompare(b.delivery_date ?? ""),
    },
    {
      title: "Received at",
      dataIndex: "received_at",
      width: 150,
      render: (d: string | null) =>
        d ? formatCompactDateTime(d) : <Text type="secondary">—</Text>,
      sorter: (a, b) =>
        (a.received_at ?? "").localeCompare(b.received_at ?? ""),
    },
    {
      title: "Lines",
      dataIndex: "lines",
      align: "right",
      width: 80,
      render: (lines: ProcurementRow["lines"]) => lines.length,
      sorter: (a, b) => a.lines.length - b.lines.length,
    },
    {
      title: "Total cost",
      dataIndex: "total_cost_cents",
      align: "right",
      render: formatCents,
      sorter: (a, b) => a.total_cost_cents - b.total_cost_cents,
    },
    {
      title: "Total Pfand",
      dataIndex: "total_pfand_cents",
      align: "right",
      render: formatCents,
      sorter: (a, b) => a.total_pfand_cents - b.total_pfand_cents,
    },
    {
      title: "Updated",
      dataIndex: "updated_at",
      width: 130,
      render: formatCompactDateTime,
      // Default sort comes from `defaultSortOrder` below.
      sorter: (a, b) => a.updated_at.localeCompare(b.updated_at),
      defaultSortOrder: "descend",
    } as ColumnType<ProcurementRow>,
    {
      title: "Bill total",
      dataIndex: "bill_total_cents",
      align: "right",
      width: 130,
      render: formatCents,
      sorter: (a, b) => a.bill_total_cents - b.bill_total_cents,
    },
  ];

  return (
    <Card>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Procurement
        </Title>
        <Button type="primary" onClick={() => void onCreateDraft()} loading={creating}>
          + New Draft
        </Button>
      </Space>

      {/* Date-range filter — kept above the table because it's a range,
          not a column-level predicate. State + Supplier moved into
          per-column filterDropdowns (see `columns` above). */}
      <Space
        size="middle"
        style={{ marginBottom: 16, flexWrap: "wrap", display: "flex" }}
      >
        <Space size={4} align="center">
          <span style={{ color: "rgba(0,0,0,0.55)" }}>Delivery:</span>
          <DatePicker
            value={dateFrom ? dayjs(dateFrom) : null}
            onChange={(d) => setDateRange(d ? d.format("YYYY-MM-DD") : "", dateTo)}
            placeholder="From"
            format="DD.MM.YYYY"
          />
          <DatePicker
            value={dateTo ? dayjs(dateTo) : null}
            onChange={(d) => setDateRange(dateFrom, d ? d.format("YYYY-MM-DD") : "")}
            placeholder="To"
            format="DD.MM.YYYY"
          />
        </Space>
      </Space>

      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      <Table<ProcurementRow>
        columns={columns}
        dataSource={filteredRows}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: false }}
        onChange={(_pagination, filters) => {
          // Reflect AntD's column-filter state back into the URL so the
          // filter survives reload. Both updates coalesce into one
          // `replace` call so the URL changes once per interaction.
          const nextState = Array.isArray(filters.state)
            ? (filters.state.filter(
                (s): s is ProcurementState =>
                  s === "draft" || s === "received" || s === "cancelled",
              ) as ProcurementState[])
            : [];
          const nextSupplier =
            Array.isArray(filters.supplier) && filters.supplier.length > 0
              ? String(filters.supplier[0])
              : "";
          replace({
            filters: {
              ...urlState.filters,
              state: nextState.join(","),
              supplier: nextSupplier,
            },
          });
        }}
        onRow={(row) => ({
          onClick: () => navigate(`/stock/procurement/${row.id}`),
          style: { cursor: "pointer" },
        })}
        summary={(pageData) => {
          // Sum across the currently-displayed rows — pageData reflects
          // EVERY filter (column-level + the external date range above)
          // AND the current pagination page, so the totals match what the
          // operator can actually see in the table.
          const cost = pageData.reduce((s, r) => s + r.total_cost_cents, 0);
          const pfand = pageData.reduce((s, r) => s + r.total_pfand_cents, 0);
          const lines = pageData.reduce((s, r) => s + r.lines.length, 0);
          const billTotal = pageData.reduce(
            (s, r) => s + r.bill_total_cents,
            0,
          );
          // Cell indexes match the columns array order above:
          //   0=#, 1=State, 2=Supplier, 3=Delivery date, 4=Received at,
          //   5=Lines, 6=Total cost, 7=Total Pfand, 8=Updated, 9=Bill total.
          // The Σ label spans #…Received at (5 columns); Lines /
          // Total cost / Total Pfand each get their own summed cell;
          // Updated stays empty; Bill total gets its own summed cell.
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <Text strong>
                  Σ {pageData.length} row{pageData.length === 1 ? "" : "s"}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                <Text strong>{lines}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                <Text strong>{formatCents(cost)}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">
                <Text strong>{formatCents(pfand)}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} />
              <Table.Summary.Cell index={9} align="right">
                <Text strong>{formatCents(billTotal)}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
    </Card>
  );
};

export default ProcurementPage;
